<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { cn } from '../../libs/utils';
import type { WithClassAsProps } from './interface';
import { useCarousel } from './useCarousel';

const props = withDefaults(
  defineProps<
    {
      clickToIndex?: boolean;
    } & WithClassAsProps
  >(),
  {
    clickToIndex: true,
  },
);
const emit = defineEmits(['click']);

const { currentIndex, itemsCount, scrollTo } = useCarousel();
const itemRef = ref<HTMLElement>();
const itemIndex = ref<number>(0);

const isActive = computed(() => currentIndex.value === itemIndex.value);
const isFirst = computed(() => itemIndex.value === 0);
const isLast = computed(() => itemIndex.value === itemsCount.value - 1);

const handleClick = () => {
  if (props.clickToIndex) {
    scrollTo(itemIndex.value);
  } else {
    emit('click', itemIndex.value);
  }
};

onMounted(() => {
  if (itemRef.value) {
    const swiperSlides = itemRef.value
      .closest('swiper-container')
      ?.querySelectorAll('swiper-slide');

    if (swiperSlides) {
      itemIndex.value = Array.from(swiperSlides as NodeListOf<Element>).indexOf(itemRef.value);
    }
  }
});
</script>

<template>
  <swiper-slide
    ref="itemRef"
    :class="cn('flex items-center justify-center', props.class)"
    role="group"
    aria-roledescription="slide"
    @click="handleClick"
  >
    <slot
      name="default"
      :index="itemIndex"
      :is-active="isActive"
      :is-first="isFirst"
      :is-last="isLast"
      :current-index="currentIndex"
      :items-count="itemsCount"
    />
  </swiper-slide>
</template>
